/**
 * FormValidator - one instance per <form>, wired to its submit/change/input.
 *
 * Rules are read from `validate-*` attributes on the field itself (the value
 * holder: the original <select> for selekt, the holder <input> for
 * date-selekt, the hidden jsonb input for file-selekt - i.e. the element that
 * fires the native `change`). Reserved suffixes:
 *   validate-<rule>-message   per-field message override
 *   validate-<rule>-attach    comma-separated field NAMES whose values are
 *                             passed to the rule (ctx.attach) / to the server
 *
 * Behavior:
 *   - change: validate that field only (server rule included, result cached
 *     per value+attach so submit does not re-request)
 *   - input: only fields currently showing an error re-validate live (sync
 *     rules; the server result comes from the cache or clears until change)
 *   - validate-on="keyup|input|blur" on a field adds an EARLIER full-validation
 *     trigger (change always validates too). The typing triggers (keyup/input)
 *     are debounced (defaults.debounce ms) and skip when the value did not
 *     really change; a new server request aborts the previous in-flight one.
 *   - submit: ALWAYS preventDefault (forms go to the API via g.fetch, never a
 *     classic POST). All fields validate; on errors -> g.error toast + focus
 *     first invalid + `validator:invalid`; when clean -> `validator:submit`
 *     (both bubble). App code listens for validator:submit and does g.fetch.
 *
 * Disabled fields and fields whose component wrapper is not rendered (hidden
 * conditional sections) are skipped - and their stale errors cleared.
 */
import { registry, isEmpty } from './rules.js';

const t = (text) => window.g.translate(text);

const WRAPPERS = '.selekt, .dsel, .fsel, .gedit';

const warned = new Set();

// Tweakable via g.validator.defaults.<key> = ...
export const defaults = {
	on: 'change',   // per-field override: validate-on="change|input|keyup|blur"
	debounce: 150,  // ms of typing silence before keyup/input-triggered validation runs
};

// The value of a field, normalized per control type: checkbox group /
// multi-select -> array of values, radio group -> checked value or '',
// file-selekt holder -> parsed jsonb (object or array), rest -> el.value.
export function fieldValue(el, form) {
	const type = el.type;
	// editor: the holder's value is HTML - rules run on the plain TEXT (a
	// required/min-length that counted tags would lie to the user)
	if (el.editor && el.closest('.gedit')) return el.editor.text();
	// file-selekt: the attributed element is the picker; the jsonb value lives
	// in the hidden holder the library created next to it.
	const fselWrap = el.closest('.fsel');
	if (fselWrap) {
		const holder = (el.fileSelekt && el.fileSelekt.holder) || fselWrap.querySelector('input[type="hidden"]');
		const raw = holder ? holder.value : el.value;
		if (!raw) return '';
		try {
			const parsed = JSON.parse(raw);
			return parsed == null ? '' : parsed;
		} catch {
			return raw;
		}
	}
	if (type === 'checkbox' || type === 'radio') {
		const group = el.name && form ? Array.from(form.querySelectorAll('input[type="' + type + '"][name="' + CSS.escape(el.name) + '"]')) : [el];
		if (type === 'radio' || group.length > 1) {
			const checked = group.filter((c) => c.checked).map((c) => c.value);
			return type === 'radio' ? (checked[0] ?? '') : checked;
		}
		return el.checked ? el.value || 'on' : '';
	}
	if (el instanceof HTMLSelectElement && el.multiple) return Array.from(el.selectedOptions).map((o) => o.value);
	if (type === 'file') return el.files ? Array.from(el.files) : [];
	return el.value;
}

// validate-* attributes -> { rules: [{name, param}], messages, attach }.
// Order: required first, server last, the rest keep attribute order.
function parseRules(el) {
	const rules = [];
	const messages = {};
	const attach = {};
	for (const attr of el.attributes) {
		let name = attr.name;
		if (name.startsWith('data-validate-')) name = name.slice(14);
		else if (name.startsWith('validate-')) name = name.slice(9);
		else continue;
		if (name === 'on') continue; // validate-on = trigger config, not a rule
		if (name.endsWith('-message')) messages[name.slice(0, -8)] = attr.value;
		else if (name.endsWith('-attach')) attach[name.slice(0, -7)] = attr.value.split(',').map((s) => s.trim()).filter(Boolean);
		else rules.push({ name, param: attr.value });
	}
	rules.sort((a, b) => weight(a.name) - weight(b.name));
	return { rules, messages, attach };
}

const weight = (name) => (name === 'required' ? -1 : name === 'server' ? 1 : 0);

function labelOf(el) {
	if (el.id) {
		const label = document.querySelector('label[for="' + CSS.escape(el.id) + '"]');
		if (label) return label.textContent.trim();
	}
	return el.name || '';
}

// Is this field (or its component wrapper) currently marked invalid?
function hasError(el) {
	const root = el.closest(WRAPPERS) || el;
	const box = root.nextElementSibling;
	return root.classList.contains('is-invalid') || root.classList.contains('genus-invalid') || !!(box && box.classList && box.classList.contains('genus-field-error'));
}

export class FormValidator {
	constructor(form) {
		this.form = form;
		form.validator = this;
		form.setAttribute('novalidate', '');
		this.serverCache = new WeakMap();
		this.serverAbort = new WeakMap();
		this.debounces = new WeakMap();
		this.lastValidated = new WeakMap();
		this.submitting = false;
		this.resetting = false;
		this.resetTimer = null;
		form.addEventListener('submit', (e) => this.onSubmit(e));
		form.addEventListener('change', (e) => this.onChange(e));
		form.addEventListener('input', (e) => this.onInput(e));
		form.addEventListener('keyup', (e) => this.onKeyup(e));
		form.addEventListener('focusout', (e) => this.onBlur(e));
		form.addEventListener('reset', () => this.onReset());
	}

	// form.reset(): our components restore their values asynchronously and
	// fire change - swallow those for a moment and wipe every error, so a
	// freshly reset form never shows "required" marks.
	onReset() {
		this.resetting = true;
		this.reset();
		clearTimeout(this.resetTimer);
		this.resetTimer = setTimeout(() => {
			this.resetTimer = null;
			this.resetting = false;
			this.reset();
		}, 250);
	}

	// Every form control carrying at least one validate-* attribute.
	fields() {
		return Array.from(this.form.elements).filter((el) => {
			for (const attr of el.attributes) {
				if (attr.name.startsWith('validate-') || attr.name.startsWith('data-validate-')) return true;
			}
			return false;
		});
	}

	// Map any event target (a checkbox of a group, an inner component input)
	// back to the attributed field it belongs to.
	findField(target) {
		if (!(target instanceof Element)) return null;
		const fields = this.fields();
		if (fields.includes(target)) return target;
		if (target.name) {
			const byName = fields.find((f) => f.name === target.name);
			if (byName) return byName;
		}
		const wrapper = target.closest(WRAPPERS);
		if (wrapper) return fields.find((f) => wrapper.contains(f)) || null;
		return null;
	}

	// Skipped = disabled, or the visible root is not rendered (conditional
	// sections). The component holders themselves are hidden by design, so
	// visibility is checked on the wrapper.
	skips(el) {
		if (el.matches(':disabled')) return true;
		const root = el.closest(WRAPPERS) || el;
		return root.getClientRects().length === 0;
	}

	// The field's full-validation trigger: validate-on attribute or the default.
	triggerOf(el) {
		return el.getAttribute('validate-on') || defaults.on;
	}

	onChange(e) {
		if (this.resetting) return;
		// file-selekt's raw picker fires change while the upload is still
		// running - the value lands in the holder, whose change we validate on.
		if (e.target.type === 'file' && e.target.closest('.fsel')) return;
		const field = this.findField(e.target);
		if (!field) return;
		clearTimeout(this.debounces.get(field)); // change supersedes a queued typing validation
		this.validateField(field);
	}

	onInput(e) {
		if (this.resetting) return;
		const field = this.findField(e.target);
		if (!field) return;
		if (this.triggerOf(field) === 'input') this.queueValidate(field);
		else if (hasError(field)) this.validateField(field, true);
	}

	onKeyup(e) {
		if (this.resetting) return;
		// Only keys that can change the value - otherwise tabbing INTO a
		// pristine field would validate it (and mark it required) instantly.
		if (e.key && e.key.length !== 1 && e.key !== 'Backspace' && e.key !== 'Delete') return;
		const field = this.findField(e.target);
		if (field && this.triggerOf(field) === 'keyup') this.queueValidate(field);
	}

	onBlur(e) {
		if (this.resetting) return;
		const field = this.findField(e.target);
		if (field && this.triggerOf(field) === 'blur') this.validateField(field);
	}

	// Debounced full validation for the typing triggers: waits for a pause,
	// then skips entirely if the value is still the one last validated.
	queueValidate(el) {
		clearTimeout(this.debounces.get(el));
		this.debounces.set(el, setTimeout(() => {
			this.debounces.delete(el);
			if (this.resetting) return;
			if (JSON.stringify(fieldValue(el, this.form)) === this.lastValidated.get(el)) return;
			this.validateField(el);
		}, defaults.debounce));
	}

	onSubmit(e) {
		// Forms are never submitted natively - the app sends them via g.fetch
		// from its validator:submit handler.
		e.preventDefault();
		// a real submit supersedes a pending reset settle: the delayed wipe
		// (onReset's 250ms timer) must never eat errors this submission
		// applies after it - reset then submit-within-250ms used to lose the
		// server's data.fields marks
		clearTimeout(this.resetTimer);
		this.resetTimer = null;
		this.resetting = false;
		if (this.submitting) return;
		this.submitting = true;
		this.validateAll().then(({ valid, invalid }) => {
			this.submitting = false;
			if (valid) {
				this.form.dispatchEvent(new CustomEvent('validator:submit', { bubbles: true, detail: { validator: this } }));
				return;
			}
			// deliberate auto-hide (errors are sticky by default): the lasting
			// record IS the inline field errors, the toast is just the pointer
			window.g.error(t('The form contains errors. Please review the highlighted fields.'), { duration: 5000 });
			this.focusField(invalid[0]);
			this.form.dispatchEvent(new CustomEvent('validator:invalid', { bubbles: true, detail: { validator: this, fields: invalid } }));
		});
	}

	async validateAll() {
		return this.validateFields(this.fields());
	}

	// Validate only the fields inside a DOM container (a wizard step, a section):
	// same rules as validateAll but scoped. Returns { valid, invalid }.
	async validateWithin(container) {
		const root = typeof container === 'string' ? document.querySelector(container) : container;
		return this.validateFields(root ? this.fields().filter((el) => root.contains(el)) : []);
	}

	async validateFields(els) {
		const results = await Promise.all(els.map((el) => this.validateField(el)));
		const invalid = els.filter((el, i) => !results[i]);
		return { valid: invalid.length === 0, invalid };
	}

	// Validate one field and show/clear its error. `live` = called from
	// `input` while the field already has an error: sync rules only, the
	// server result comes from the cache (no request per keystroke).
	async validateField(el, live = false) {
		const parsed = parseRules(el);
		if (!parsed.rules.length) return true;
		if (this.skips(el)) {
			window.g.fieldError(el, null);
			return true;
		}
		const seq = (el._validatorSeq = (el._validatorSeq || 0) + 1);
		const value = fieldValue(el, this.form);
		let message = null;

		for (const rule of parsed.rules) {
			if (rule.name !== 'required' && isEmpty(value)) continue;
			const attach = this.attachValues(parsed.attach[rule.name]);

			if (rule.name === 'server') {
				let result;
				if (live) {
					result = this.cachedServer(el, rule.param, value, attach);
					if (result === undefined) continue; // not asked yet - change/submit will
				} else {
					result = await this.serverValidate(el, rule.param, value, attach);
				}
				if (result !== true) {
					message = result;
					break;
				}
				continue;
			}

			const def = registry[rule.name];
			if (!def) {
				if (!warned.has(rule.name)) {
					warned.add(rule.name);
					console.warn('validator: unknown rule "' + rule.name + '"');
				}
				continue;
			}
			let result = def.validate(value, {
				el,
				form: this.form,
				rule: rule.name,
				param: rule.param === '' ? true : rule.param,
				attach,
				valueOf: (name) => {
					const other = this.form.querySelector('[name="' + CSS.escape(name) + '"]');
					return other ? fieldValue(other, this.form) : undefined;
				},
			});
			if (result && typeof result.then === 'function') result = await result;
			if (result === true) continue;
			// attribute-provided messages translate at runtime (project
			// strings); the SHIPPED registry defaults are THUNKS around
			// literal t('…') calls (rules.js — scannable, cursuri #58);
			// project-registered rules keep the documented plain-string
			// message, translated at runtime like an attribute message
			message = typeof result === 'string' && result ? result : this.format(
				parsed.messages[rule.name] != null ? t(parsed.messages[rule.name])
					: typeof def.message === 'function' ? def.message()
					: def.message ? t(def.message)
					: t('Invalid value.'), el, rule, value);
			break;
		}

		if (el._validatorSeq !== seq) return message == null; // superseded by a newer run
		this.lastValidated.set(el, JSON.stringify(value));
		window.g.fieldError(el, message);
		return message == null;
	}

	// { fieldName: value } for a validate-<rule>-attach="a,b" list.
	attachValues(names) {
		const out = {};
		for (const name of names || []) {
			const el = this.form.querySelector('[name="' + CSS.escape(name) + '"]');
			if (el) out[name] = fieldValue(el, this.form);
		}
		return out;
	}

	format(template, el, rule, value) {
		// the template arrives already translated — this only fills the
		// placeholders (translating here would re-translate a translation)
		const map = { param: rule.param, value: Array.isArray(value) ? value.join(', ') : String(value ?? ''), label: labelOf(el) };
		return template.replace(/\{(param|value|label)\}/g, (m, key) => (map[key] != null ? map[key] : m));
	}

	// validate-server="/endpoint" via g.fetch. Sends { field, value, attach },
	// expects data: { valid, message? }; error.code > 0 -> error.message is the
	// field error. System errors (API down) block too - if validation cannot
	// run, neither can the submit - and are NOT cached, so a retry re-asks.
	async serverValidate(el, endpoint, value, attach) {
		const key = JSON.stringify([endpoint, value, attach]);
		let cache = this.serverCache.get(el);
		if (!cache) {
			cache = new Map();
			this.serverCache.set(el, cache);
		}
		if (cache.has(key)) return cache.get(key);
		// A newer check for this field makes the previous in-flight one moot.
		const prev = this.serverAbort.get(el);
		if (prev) prev.abort();
		const ctrl = new AbortController();
		this.serverAbort.set(el, ctrl);
		// progress: false - per-field checks (possibly per keystroke) render
		// under the field, not on the app-wide top bar.
		const res = await window.g.fetch(endpoint, { field: el.name || el.id || '', value, attach }, { signal: ctrl.signal, progress: false, unguarded: true }); // the abort controller is the guard (latest wins)
		if (this.serverAbort.get(el) === ctrl) this.serverAbort.delete(el);
		let result;
		if (!res.success && res.error && res.error.code === 4) result = true; // aborted: the newer run owns the field
		else if (!res.success) result = t('Could not validate this field. Please try again.');
		else if (res.error && res.error.code > 0) result = res.error.message || t('Invalid value.'); // business error: already translated by the API
		else if (res.data && res.data.valid) result = true;
		else result = res.data && res.data.message ? t(res.data.message) : t('Invalid value.'); // validation result: a canonical interface string, translated here
		if (res.success) cache.set(key, result);
		return result;
	}

	cachedServer(el, endpoint, value, attach) {
		const cache = this.serverCache.get(el);
		return cache ? cache.get(JSON.stringify([endpoint, value, attach])) : undefined;
	}

	focusField(el) {
		if (!el) return;
		const root = el.closest(WRAPPERS) || el;
		const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
		root.scrollIntoView({ behavior: reduced ? 'auto' : 'smooth', block: 'center' });
		const target = root === el && el.type !== 'hidden' ? el : root.querySelector('input:not([type="hidden"]), select, textarea, button, [tabindex]');
		if (target && target.focus) target.focus({ preventScroll: true });
	}

	// Clear every field error shown by this validator.
	reset() {
		for (const el of this.fields()) window.g.fieldError(el, null);
	}
}
